import { PerfilSeccionId } from '../../data/perfil-microempresario.content';
import { valueKeyFromTipoId } from '../documentos/perfil-documentos.util';
import {
  PerfilCargaSolicitud,
  PerfilEmpresarioCache,
  PerfilRedSocialDetalle,
} from '../solicitud/perfil-solicitud.models';
import {
  PerfilDocumentoDef,
  PerfilProgressValues,
  UploadedTipoDocumento,
} from './perfil-progress.definitions';

export function toInputDate(value: unknown): string {
  if (!value) {
    return '';
  }
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
    return value.slice(0, 10);
  }
  try {
    return new Date(value as string).toISOString().slice(0, 10);
  } catch {
    return '';
  }
}

function siNoFromFlag(value: unknown): string {
  if (value === true || value === 1 || value === '1' || value === 'si') {
    return 'si';
  }
  if (value === false || value === 0 || value === '0' || value === 'no') {
    return 'no';
  }
  return '';
}

export function mapEmpresarioToSobreTi(
  emp: PerfilEmpresarioCache | null | undefined
): PerfilProgressValues {
  if (!emp) {
    return {};
  }
  const values: PerfilProgressValues = {};
  if (emp.idGenero != null && emp.idGenero !== '') {
    // String: select/selectpicker emiten string; evita dirty falso (1 vs "1").
    values['genero'] = String(emp.idGenero);
  }
  if (emp.fechaNacimiento) {
    values['fechaNacimiento'] = toInputDate(emp.fechaNacimiento);
  }
  if (emp.fechaExpedicionDocumento) {
    values['fechaExpedicion'] = toInputDate(emp.fechaExpedicionDocumento);
  }
  if (emp.idEstadoCivil) {
    values['estadoCivil'] = String(emp.idEstadoCivil);
  }
  if (emp.idNivelEstudios) {
    values['nivelEstudios'] = String(emp.idNivelEstudios);
  }
  if (emp.personasACargo != null && emp.personasACargo !== '') {
    values['personasACargo'] = String(emp.personasACargo);
  }
  if (emp.seguridadSocial != null && emp.seguridadSocial !== '') {
    const ss = Number(emp.seguridadSocial);
    values['seguridadSocial'] =
      ss === 1 || String(emp.seguridadSocial) === 'si' ? 'si' : 'no';
  }
  if (emp.sisben != null) {
    values['sisben'] = siNoFromFlag(emp.sisben) || 'no';
  }
  if (emp.idSisben != null) {
    values['idSisben'] = String(emp.idSisben);
  }
  if (emp.creditoAnterior != null) {
    values['creditoFormal'] = Number(emp.creditoAnterior) === 1 ? 'si' : 'no';
  }
  if (emp.fechaCreditoAnterior) {
    values['fechaDesembolso'] = toInputDate(emp.fechaCreditoAnterior);
  }
  return values;
}

export function mapEmpresarioToSobreTiJuridicaExtras(
  emp: PerfilEmpresarioCache | null | undefined
): PerfilProgressValues {
  if (!emp) {
    return {};
  }
  const values: PerfilProgressValues = {};
  const minoria = siNoFromFlag(emp.minoria);
  if (minoria) {
    values['minoria'] = minoria;
  }
  if (emp.minoriaDescripcion) {
    values['minoriaDescripcion'] = String(emp.minoriaDescripcion);
  }
  if (emp.razonSocial) {
    values['razonSocial'] = emp.razonSocial;
  }
  const ciiu =
    emp.codigoCiiu != null && emp.codigoCiiu !== ''
      ? String(emp.codigoCiiu)
      : emp.ciiuNombre
        ? String(emp.ciiuNombre)
        : '';
  if (ciiu) {
    values['actividadEconomica'] = ciiu;
  }
  if (emp.idTipoSociedad != null && emp.idTipoSociedad !== '') {
    values['tipoSociedad'] = String(emp.idTipoSociedad);
  }
  if (emp.camaraComercioVigente != null) {
    values['tieneCamaraComercio'] =
      Number(emp.camaraComercioVigente) === 1 ||
      emp.camaraComercioVigente === true ||
      String(emp.camaraComercioVigente).toLowerCase() === 'si'
        ? 'si'
        : 'no';
  }
  if (emp.fechaRegistroActividadEconomica) {
    values['fechaRegistroCamara'] = toInputDate(
      emp.fechaRegistroActividadEconomica
    );
  }
  if (emp.idDepartamento) {
    values['departamento'] = String(emp.idDepartamento);
  }
  if (emp.idCiudad != null) {
    values['ciudad'] = String(emp.idCiudad);
  }
  if (emp.idLocalidad) {
    values['localidad'] = String(emp.idLocalidad);
  }
  const barrio =
    (emp.barrioEmpresa as string | undefined) || emp.barrioResidencia;
  if (barrio) {
    values['barrio'] = barrio;
  }
  const direccion =
    (emp.direccionEmpresa as string | undefined) || emp.direccionResidencia;
  if (direccion) {
    values['direccion'] = direccion;
  }
  return values;
}

/** Hydrate completo PJ: subset personal/crédito + empresa. */
export function mapEmpresarioToSobreTiJuridica(
  emp: PerfilEmpresarioCache | null | undefined
): PerfilProgressValues {
  return {
    ...mapEmpresarioToSobreTi(emp),
    ...mapEmpresarioToSobreTiJuridicaExtras(emp),
  };
}

export function mapEmpresarioToSobreNegocio(
  emp: PerfilEmpresarioCache | null | undefined
): PerfilProgressValues {
  if (!emp) {
    return {};
  }
  const values: PerfilProgressValues = {};
  if (emp.razonSocial) {
    values['nombreNegocio'] = emp.razonSocial;
  }
  if (emp.idDepartamento) {
    values['departamento'] = String(emp.idDepartamento);
  }
  if (emp.idCiudad != null) {
    values['ciudad'] = String(emp.idCiudad);
  }
  if (emp.idLocalidad) {
    values['localidad'] = String(emp.idLocalidad);
  }
  if (emp.barrioResidencia) {
    values['barrio'] = emp.barrioResidencia;
  }
  if (emp.direccionResidencia) {
    values['direccion'] = emp.direccionResidencia;
  }
  if (emp.idTipoVivienda) {
    values['tipoVivienda'] = String(emp.idTipoVivienda);
  }
  if (emp.tieneRUT != null && emp.tieneRUT !== '') {
    const rut = String(emp.tieneRUT).toLowerCase();
    values['tieneRut'] =
      rut === '1' || rut === 'si' || rut === 'true' ? 'si' : 'no';
  }
  if (emp.camaraComercioVigente != null) {
    values['tieneCamaraComercio'] =
      Number(emp.camaraComercioVigente) === 1 ? 'si' : 'no';
  }
  if (emp.fechaRegistroActividadEconomica) {
    values['fechaRegistroCamara'] = toInputDate(
      emp.fechaRegistroActividadEconomica
    );
  }
  return values;
}

export function mapEmpresarioToSobreIngresos(
  emp: PerfilEmpresarioCache | null | undefined
): PerfilProgressValues {
  if (!emp) {
    return {};
  }
  const values: PerfilProgressValues = {};
  if (emp.anioActivos != null && emp.anioActivos !== '') {
    values['anioActivos'] = String(emp.anioActivos);
  }
  if (emp.nivelActivos != null && emp.nivelActivos !== '') {
    values['valorActivos'] = emp.nivelActivos;
  }
  if (emp.fechaRegistroActividadEconomica) {
    values['fechaRegistroActividadEconomica'] =
      emp.fechaRegistroActividadEconomica;
  }
  if (emp.valorVentas != null && emp.valorVentas !== '') {
    values['valorVentas'] = emp.valorVentas;
    values['ventasUltimoAnio'] = emp.valorVentas;
  }
  const declaraRenta = siNoFromFlag(emp.declaraRenta);
  if (declaraRenta) {
    values['declaraRenta'] = declaraRenta;
  }
  if (emp.flujoCajaMesCorteInformacion) {
    values['mesReporte'] = emp.flujoCajaMesCorteInformacion;
  }
  if (emp.flujoCajaVentas != null) {
    values['ventasUltimoMes'] = emp.flujoCajaVentas;
  }
  if (emp.flujoCajaOtrosIngresos != null) {
    values['otrosIngresos'] = emp.flujoCajaOtrosIngresos;
  }
  if (emp.flujoCajaFuenteOtrosIngresos) {
    values['fuenteOtrosIngresos'] = emp.flujoCajaFuenteOtrosIngresos;
  }
  if (emp.flujoCajaPagoProveedores != null) {
    values['pagoProveedores'] = emp.flujoCajaPagoProveedores;
  }
  if (emp.flujoCajaArriendo != null) {
    values['arriendo'] = emp.flujoCajaArriendo;
  }
  if (emp.flujoCajaAdministracion != null) {
    values['administracion'] = emp.flujoCajaAdministracion;
  }
  if (emp.flujoCajaServiciosPublicos != null) {
    values['serviciosPublicosNegocio'] = emp.flujoCajaServiciosPublicos;
  }
  if (emp.flujoCajaSueldoPropio != null) {
    values['sueldoPropio'] = emp.flujoCajaSueldoPropio;
  }
  if (emp.flujoCajaSueldoEmpleados != null) {
    values['sueldoEmpleados'] = emp.flujoCajaSueldoEmpleados;
  }
  if (emp.flujoCajaPlanCelular != null) {
    values['planCelular'] = emp.flujoCajaPlanCelular;
  }
  if (emp.flujoCajaTarjetaCredito != null) {
    values['tarjetaCredito'] = emp.flujoCajaTarjetaCredito;
  }
  if (emp.flujoCajaCuotaCreditos != null) {
    values['cuotaCreditos'] = emp.flujoCajaCuotaCreditos;
    if (Number(emp.flujoCajaCuotaCreditos) > 0) {
      values['tieneCreditos'] = 'si';
    }
  }
  if (emp.flujoCajaSaldoDeudas != null) {
    values['saldoDeudas'] = emp.flujoCajaSaldoDeudas;
  }
  if (emp.flujoCajaOtrosGastos != null) {
    values['otrosGastos'] = emp.flujoCajaOtrosGastos;
  }
  if (emp.flujoCajaFuenteOtrosGastos) {
    values['fuenteOtrosGastos'] = emp.flujoCajaFuenteOtrosGastos;
  }
  return values;
}

export function mapUploadedDocsToValues(
  uploaded: ReadonlyArray<UploadedTipoDocumento>,
  documentos: ReadonlyArray<PerfilDocumentoDef>,
  seccionId: PerfilSeccionId
): PerfilProgressValues {
  const values: PerfilProgressValues = {};
  const sectionDocs = documentos.filter((d) => d.seccionId === seccionId);
  for (const item of uploaded) {
    const def = sectionDocs.find(
      (d) => Number(d.idTipoDocumento ?? d.id) === Number(item.idTipoDocumento)
    );
    if (!def) {
      continue;
    }
    const validFile = (item.neoDocumentosSolicitudDTO ?? []).find((file) => {
      if (!file?.idDocumentoSolicitud && !file?.nombreDocumento) {
        return false;
      }
      const ocrFail =
        file.estadoDocumentoOCR != null &&
        Number(file.estadoDocumentoOCR) === 0;
      const habFail =
        file.habilitado != null && Number(file.habilitado) === 0;
      return !ocrFail && !habFail;
    });
    if (!validFile) {
      continue;
    }
    // Completo con ≥1 archivo válido (multiple: no exige llenar N slots).
    const key = def.valueKey || valueKeyFromTipoId(def.idTipoDocumento);
    values[key] = validFile.nombreDocumento || 'Documento';
  }
  return values;
}

export function unwrapDocumentosSolicitudResponse(raw: unknown): {
  archivos: UploadedTipoDocumento[];
  fotos: UploadedTipoDocumento[];
} {
  if (!raw || typeof raw !== 'object') {
    return { archivos: [], fotos: [] };
  }
  const root = raw as {
    archivos?: UploadedTipoDocumento[];
    fotos?: UploadedTipoDocumento[];
    data?: {
      archivos?: UploadedTipoDocumento[];
      fotos?: UploadedTipoDocumento[];
    };
  };
  const data =
    Array.isArray(root.archivos) || Array.isArray(root.fotos)
      ? root
      : root.data ?? {};
  return {
    archivos: Array.isArray(data.archivos) ? data.archivos : [],
    fotos: Array.isArray(data.fotos) ? data.fotos : [],
  };
}

export function flattenUploadedFromRaw(raw: unknown): UploadedTipoDocumento[] {
  if (
    raw &&
    typeof raw === 'object' &&
    Array.isArray((raw as { archivos?: unknown }).archivos) &&
    !('exitoso' in (raw as object))
  ) {
    const data = raw as {
      archivos?: UploadedTipoDocumento[];
      fotos?: UploadedTipoDocumento[];
    };
    return [...(data.archivos ?? []), ...(data.fotos ?? [])];
  }
  const data = unwrapDocumentosSolicitudResponse(raw);
  return [...data.archivos, ...data.fotos];
}

export function buildProgressValuesFromBackend(
  carga: PerfilCargaSolicitud,
  documentos: ReadonlyArray<PerfilDocumentoDef>,
  uploadedRaw: unknown
): Partial<Record<PerfilSeccionId, PerfilProgressValues>> {
  const uploaded = flattenUploadedFromRaw(uploadedRaw);
  return {
    'sobre-ti': {
      ...mapEmpresarioToSobreTi(carga.empresario),
      ...mapEmpresarioToSobreTiJuridicaExtras(carga.empresario),
      ...mapUploadedDocsToValues(uploaded, documentos, 'sobre-ti'),
    },
    'sobre-negocio': {
      ...mapEmpresarioToSobreNegocio(carga.empresario),
      ...mapUploadedDocsToValues(uploaded, documentos, 'sobre-negocio'),
    },
    'sobre-ingresos': {
      ...mapEmpresarioToSobreIngresos(carga.empresario),
      autorizacionCentrales: !!carga.solicitud?.apectacionCentrales,
      ...mapUploadedDocsToValues(uploaded, documentos, 'sobre-ingresos'),
    },
  };
}

export function findRedDetalleForCatalogo(
  detalle: unknown,
  idCatalogo: number | null | undefined,
  codigo: string
): PerfilRedSocialDetalle | undefined {
  if (!Array.isArray(detalle)) {
    return undefined;
  }
  const rows = detalle as PerfilRedSocialDetalle[];
  if (idCatalogo != null && Number.isFinite(Number(idCatalogo))) {
    return rows.find(
      (d) =>
        d.idCatalogoRedSocial != null &&
        Number(d.idCatalogoRedSocial) === Number(idCatalogo)
    );
  }
  const code = (codigo || '').toLowerCase().trim();
  if (code.length < 3) {
    return undefined;
  }
  return rows.find((d) => {
    if (d.idCatalogoRedSocial != null) {
      return false;
    }
    return (d.url || '').toLowerCase().includes(code);
  });
}
